import TableFieldSchema from "./TableFieldSchema";
import {schemas} from "../../../config";
import {useAppDispatch, useAppSelector} from "../../../hooks";
import styled from "styled-components";
import React, {useRef} from "react";
import {LANG} from "../../CONSTANTS";
import {selectFilteredFields, setFrontSchemaStateField} from "../../../features/schemaManageSlice";
import {Field, TableI} from "../../../Types";
import {createPortal} from "react-dom";
import AsideHeaderFrontSchemaFunc from "./AsideHeaderFrontSchemaFunc";
import useDimensions from "../../hooks/useDimensions";
import LightModeFieldView from "./LightModeFieldView";
import {useFadeIn} from "../../hooks/useFadeIn";

const lang = LANG

type FrontFieldsParams = { tableFields: Field[], width: number };

const TILE_MIN_SIZE = 440;
const GAP = 0.5; // in rem, converted to pixels assuming 1rem = 16px

const getTileSize = (width: number, minTileSize: number) => {
    const gapInPixels = GAP * 16; // Convert rem to pixels
    const minTileSizeWithGap = minTileSize + gapInPixels;

    // Calculate the number of tiles that can fit in the current width
    const numberOfTiles = Math.floor(width / minTileSizeWithGap);

    // Calculate the actual tile size based on the number of tiles
    const totalGapSpace = (numberOfTiles - 1) * gapInPixels;
    const totalTileSpace = width - totalGapSpace;
    const tileSize = totalTileSpace / numberOfTiles;

    // Ensure the tile size is at least the minimum size
    return Math.max(tileSize, minTileSize);
};

function FrontFields({tableFields, width}: FrontFieldsParams) {
    const showUndefined = useAppSelector(state => state.schemaManage.front.showUndefined);
    const fieldsWeight = useAppSelector(state => state.schemaManage.front.showFieldsWeight);
    const renderType = useAppSelector(state => state.schemaManage.front.renderType);

    let tileSize;

    switch (renderType) {
        case "tiles":
            tileSize = getTileSize(width, 440) + "px"
            break
        case "lightMode":
            tileSize = getTileSize(width, 220) + "px"
            break
        default:
            tileSize = "100%"
    }

    const style = useFadeIn({duration: 200});

    return <ULFrontFields $liWidth={tileSize} style={style}>
        {renderType === "lightMode" && tableFields.map((f, i) => <li key={f.key} className={"light-mode"}>
            <LightModeFieldView field={f} index={i}/>
        </li>)}
        {renderType !== "lightMode" && tableFields.map(f => <li key={f.key}>
            <TableFieldSchema fieldsWeight={fieldsWeight} field={f} showUndefined={showUndefined}/>
        </li>)}
    </ULFrontFields>;
}

const ULFrontFields = styled.ul<{ $liWidth: string }>`
    display: flex;
    flex-wrap: wrap;
    align-items: stretch;
    //height: 100%;
    gap: .5rem;



    opacity: 0;

    .light-mode:nth-child(even) {
        background: var(--table-row-even-bg-color);
    }

    .light-mode:nth-child(odd) {
        background: var(--table-row-odd-bg-color);
    }

    .light-mode {
        padding: .2rem;
        border-radius: .2rem;
    }

    & li {
        width: ${props => props.$liWidth};
        //height: 100%;
        //flex-grow: 1;
        //flex-grow: 1;
    }
`

export default function FrontTableSchemaManage({ projectName, width }: { projectName: string, width: number }) {
    const selectedTable = useAppSelector(state => state.schemaManage.front.selectedTable);
    const sectionView = useAppSelector(state => state.schemaManage.front.sectionView);
    const tables = useAppSelector(state => state.schemaManage.front.tables);
    const sideHeaderReady = useAppSelector(state => state.layout.sideHeaderReady);

    const render = useAppSelector(selectFilteredFields); // 👈 now memoized & optimized

    const schema = schemas[projectName];
    const dispatch = useAppDispatch();

    return (
        <Container>
            <div className={'tables-list'}>
                <UL>
                    {Object.entries(tables).map(([tableKey, t]) => (
                        <li key={tableKey}>
                            <Btn $active={tableKey === selectedTable}
                                 onClick={() => dispatch(setFrontSchemaStateField("selectedTable", tableKey))}>
                                {t.data.title[LANG]}
                            </Btn>
                        </li>
                    ))}
                </UL>
            </div>
            {sectionView === "fields" && <FrontFields width={width} tableFields={render} />}
            {sideHeaderReady && createPortal(<AsideHeaderFrontSchemaFunc schema={schema}/>,
                document.getElementById("side-head-content")!)}
        </Container>
    );
}

const Container = styled.section`

    display: flex;
    flex-flow: column;
    gap: .5rem;
    justify-content: center;
`


const UL = styled.ul`

    padding: 0;
    display: flex;
    gap: .5rem;
    flex-wrap: wrap;
    justify-content: start;

    //border-bottom: 2px solid var(--table-action-color);

    & .toggle-side-bar {
        margin-right: auto;
    }

    & .fake {
        margin-left: auto;
    }

    & > li {
        white-space: nowrap;
        list-style: none;

        @media (max-width: 500px) {
            flex-basis: 100%;

        }
    }

    & .fake {
        width: 2.4rem;
    }



`

const Btn = styled.button<any>`
    appearance: none;
    background: transparent;
    background: var(--table-row-odd-bg-color);
    padding: .2rem .6rem;
    color: var(--table-row-text-color);
    cursor: pointer;
    width: 100%;
    font-size: 1rem;
    border: none;

    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: .2rem;
    text-align: center;

    ${props => props.$active && `border-bottom: 3px solid var(--table-action-color);`}
    ${props => !props.$active && `border-bottom: 3px solid var(--table-border-color);`}
    &:hover {
        filter: brightness(.85);
    }

    &:disabled {
        filter: grayscale(.6);
    }
`
